import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'react-barcode-scanner/polyfill';
import { normalizeScannedEan } from '../utils/eanSearch.mjs';

const FALLBACK_FORMATS = ['ean_13', 'ean_8', 'itf', 'upc_a', 'upc_e'];
const SCAN_DELAY_MS = 120;
const SUCCESS_DELAY_MS = 120;
const STABLE_HITS_REQUIRED = 2;
const MAX_STABLE_GAP_MS = 900;
const SCAN_WINDOW = {
  x: 0.08,
  y: 0.34,
  width: 0.84,
  height: 0.24,
};

function pointInRect(point, rect) {
  if (!point || !rect) return false;
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

function getBoundingRect(boundingBox, cornerPoints) {
  if (boundingBox && Number.isFinite(boundingBox.width) && Number.isFinite(boundingBox.height)) {
    return {
      x: Number(boundingBox.x || 0),
      y: Number(boundingBox.y || 0),
      width: Number(boundingBox.width || 0),
      height: Number(boundingBox.height || 0),
    };
  }

  if (Array.isArray(cornerPoints) && cornerPoints.length) {
    const xs = cornerPoints.map((p) => Number(p.x || 0));
    const ys = cornerPoints.map((p) => Number(p.y || 0));
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    return {
      x: minX,
      y: minY,
      width: Math.max(0, maxX - minX),
      height: Math.max(0, maxY - minY),
    };
  }

  return null;
}

function mapSourceRectToCanvas(rect, metrics) {
  if (!rect || !metrics) return null;

  return {
    x: rect.x * metrics.scale + metrics.offsetX,
    y: rect.y * metrics.scale + metrics.offsetY,
    width: rect.width * metrics.scale,
    height: rect.height * metrics.scale,
  };
}

function getCanvasMetrics(video, canvas) {
  if (!video || !canvas || !video.videoWidth || !video.videoHeight) return null;

  const viewportWidth = canvas.clientWidth || canvas.width || 1;
  const viewportHeight = canvas.clientHeight || canvas.height || 1;

  if (canvas.width !== viewportWidth || canvas.height !== viewportHeight) {
    canvas.width = viewportWidth;
    canvas.height = viewportHeight;
  }

  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;
  const scale = Math.max(viewportWidth / sourceWidth, viewportHeight / sourceHeight);
  const drawnWidth = sourceWidth * scale;
  const drawnHeight = sourceHeight * scale;
  const offsetX = (viewportWidth - drawnWidth) / 2;
  const offsetY = (viewportHeight - drawnHeight) / 2;

  return {
    viewportWidth,
    viewportHeight,
    scale,
    offsetX,
    offsetY,
  };
}

function getScanWindow(metrics) {
  if (!metrics) return null;

  return {
    x: metrics.viewportWidth * SCAN_WINDOW.x,
    y: metrics.viewportHeight * SCAN_WINDOW.y,
    width: metrics.viewportWidth * SCAN_WINDOW.width,
    height: metrics.viewportHeight * SCAN_WINDOW.height,
  };
}

function getRectCenter(rect) {
  if (!rect) return null;
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

function getGuideColor(scannerState) {
  switch (scannerState) {
    case 'success':
      return '#2ecc71';
    case 'candidate':
      return '#3adb76';
    case 'align':
      return '#ffb020';
    case 'error':
      return '#ef4444';
    default:
      return '#ffffff';
  }
}

function getStatusMeta(scannerState, detectedCode) {
  switch (scannerState) {
    case 'initializing':
      return {
        label: 'Kamera wird gestartet …',
        hint: 'Bitte die Kamera kurz ruhig halten.',
      };
    case 'align':
      return {
        label: 'Code erkannt',
        hint: 'Bitte mittig in den Rahmen schieben.',
      };
    case 'candidate':
      return {
        label: detectedCode ? `EAN ${detectedCode} erkannt` : 'Code erkannt',
        hint: 'Kurz ruhig halten, dann wird übernommen.',
      };
    case 'success':
      return {
        label: detectedCode ? `EAN ${detectedCode} übernommen` : 'Code übernommen',
        hint: 'Scanner schließt sich gleich.',
      };
    case 'error':
      return {
        label: 'Scanner nicht verfügbar',
        hint: 'Kamera-Zugriff prüfen und erneut öffnen.',
      };
    case 'searching':
    default:
      return {
        label: 'Suche Barcode …',
        hint: 'EAN mittig in den Rahmen halten.',
      };
  }
}

async function resolveSupportedFormats() {
  try {
    const Detector = globalThis?.BarcodeDetector;
    if (typeof Detector?.getSupportedFormats === 'function') {
      const supported = await Detector.getSupportedFormats();
      const filtered = FALLBACK_FORMATS.filter((format) => supported.includes(format));
      if (filtered.length) return filtered;
    }
  } catch {
    // Fallback unten.
  }

  return FALLBACK_FORMATS;
}

export default function BarcodeScanner({
  onDetected,
  onClose,
  scanDelay = SCAN_DELAY_MS,
  facing = 'environment',
}) {
  const videoRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const detectorRef = useRef(null);
  const timeoutRef = useRef(null);
  const streamRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLockedRef = useRef(false);
  const stableMatchRef = useRef({ code: '', hits: 0, lastSeenAt: 0 });
  const overlayStateRef = useRef({ scannerState: 'initializing', candidateRect: null, scanWindowRect: null });

  const [errMsg, setErrMsg] = useState('');
  const [scannerState, setScannerState] = useState('initializing');
  const [detectedCode, setDetectedCode] = useState('');

  const statusMeta = useMemo(() => getStatusMeta(scannerState, detectedCode), [scannerState, detectedCode]);

  const stopStream = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const drawOverlay = useCallback((nextState, candidateRect = null) => {
    const video = videoRef.current;
    const canvas = overlayCanvasRef.current;

    if (!video || !canvas) return;

    const metrics = getCanvasMetrics(video, canvas);
    if (!metrics) return;

    const scanWindowRect = getScanWindow(metrics);
    const ctx = canvas.getContext('2d');
    if (!ctx || !scanWindowRect) return;

    overlayStateRef.current = { scannerState: nextState, candidateRect, scanWindowRect };

    const guideColor = getGuideColor(nextState);
    const cornerSize = Math.min(28, scanWindowRect.height * 0.35);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = 'rgba(0, 0, 0, 0.38)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.clearRect(scanWindowRect.x, scanWindowRect.y, scanWindowRect.width, scanWindowRect.height);

    ctx.save();
    ctx.strokeStyle = guideColor;
    ctx.lineWidth = nextState === 'success' ? 5 : 4;
    ctx.shadowColor = guideColor;
    ctx.shadowBlur = nextState === 'candidate' || nextState === 'success' ? 16 : 8;

    ctx.beginPath();
    ctx.moveTo(scanWindowRect.x, scanWindowRect.y + cornerSize);
    ctx.lineTo(scanWindowRect.x, scanWindowRect.y);
    ctx.lineTo(scanWindowRect.x + cornerSize, scanWindowRect.y);

    ctx.moveTo(scanWindowRect.x + scanWindowRect.width - cornerSize, scanWindowRect.y);
    ctx.lineTo(scanWindowRect.x + scanWindowRect.width, scanWindowRect.y);
    ctx.lineTo(scanWindowRect.x + scanWindowRect.width, scanWindowRect.y + cornerSize);

    ctx.moveTo(scanWindowRect.x + scanWindowRect.width, scanWindowRect.y + scanWindowRect.height - cornerSize);
    ctx.lineTo(scanWindowRect.x + scanWindowRect.width, scanWindowRect.y + scanWindowRect.height);
    ctx.lineTo(scanWindowRect.x + scanWindowRect.width - cornerSize, scanWindowRect.y + scanWindowRect.height);

    ctx.moveTo(scanWindowRect.x + cornerSize, scanWindowRect.y + scanWindowRect.height);
    ctx.lineTo(scanWindowRect.x, scanWindowRect.y + scanWindowRect.height);
    ctx.lineTo(scanWindowRect.x, scanWindowRect.y + scanWindowRect.height - cornerSize);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = guideColor;
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 10]);
    ctx.strokeRect(scanWindowRect.x, scanWindowRect.y, scanWindowRect.width, scanWindowRect.height);
    ctx.restore();

    if (candidateRect) {
      ctx.save();
      ctx.strokeStyle = nextState === 'align' ? '#ffb020' : '#2ecc71';
      ctx.lineWidth = 3;
      ctx.shadowColor = ctx.strokeStyle;
      ctx.shadowBlur = 12;
      ctx.strokeRect(candidateRect.x, candidateRect.y, candidateRect.width, candidateRect.height);
      ctx.restore();
    }
  }, []);

  const updateUiState = useCallback((nextState, nextCode = '', candidateRect = null) => {
    setScannerState((prev) => (prev === nextState ? prev : nextState));
    setDetectedCode((prev) => (prev === nextCode ? prev : nextCode));
    drawOverlay(nextState, candidateRect);
  }, [drawOverlay]);

  const scheduleNextScan = useCallback((scanFn, delay) => {
    if (!isMountedRef.current || isLockedRef.current) return;
    timeoutRef.current = setTimeout(scanFn, delay);
  }, []);

  const scanLoop = useCallback(async () => {
    const detector = detectorRef.current;
    const video = videoRef.current;
    const canvas = overlayCanvasRef.current;

    if (!detector || !video || !canvas || isLockedRef.current) return;
    if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      updateUiState('initializing');
      scheduleNextScan(scanLoop, scanDelay);
      return;
    }

    try {
      const metrics = getCanvasMetrics(video, canvas);
      const scanWindowRect = getScanWindow(metrics);

      const detected = await detector.detect(video);
      const validDetections = (Array.isArray(detected) ? detected : [])
        .map((item) => {
          const code = normalizeScannedEan(item?.rawValue);
          const rect = getBoundingRect(item?.boundingBox, item?.cornerPoints);
          const mappedRect = mapSourceRectToCanvas(rect, metrics);
          const center = getRectCenter(mappedRect);
          const isInside = pointInRect(center, scanWindowRect);
          const widthScore = mappedRect?.width || 0;

          return code && mappedRect
            ? {
                code,
                mappedRect,
                center,
                isInside,
                widthScore,
              }
            : null;
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (a.isInside !== b.isInside) return a.isInside ? -1 : 1;
          return b.widthScore - a.widthScore;
        });

      const bestMatch = validDetections[0] || null;

      if (!bestMatch) {
        stableMatchRef.current = { code: '', hits: 0, lastSeenAt: 0 };
        updateUiState('searching', '', null);
        scheduleNextScan(scanLoop, scanDelay);
        return;
      }

      if (!bestMatch.isInside) {
        stableMatchRef.current = { code: '', hits: 0, lastSeenAt: 0 };
        updateUiState('align', bestMatch.code, bestMatch.mappedRect);
        scheduleNextScan(scanLoop, scanDelay);
        return;
      }

      const now = Date.now();
      const previous = stableMatchRef.current;
      const isSameCode = previous.code === bestMatch.code && now - previous.lastSeenAt <= MAX_STABLE_GAP_MS;

      stableMatchRef.current = {
        code: bestMatch.code,
        hits: isSameCode ? previous.hits + 1 : 1,
        lastSeenAt: now,
      };

      if (stableMatchRef.current.hits < STABLE_HITS_REQUIRED) {
        updateUiState('candidate', bestMatch.code, bestMatch.mappedRect);
        scheduleNextScan(scanLoop, scanDelay);
        return;
      }

      isLockedRef.current = true;
      updateUiState('success', bestMatch.code, bestMatch.mappedRect);

      try {
        if (window.navigator?.vibrate) {
          window.navigator.vibrate(80);
        }
      } catch {}

      timeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        onDetected?.(bestMatch.code);
      }, SUCCESS_DELAY_MS);
    } catch (error) {
      if (error?.name !== 'InvalidStateError') {
        console.warn('Barcode-Scanner Fehler:', error);
      }

      updateUiState('searching');
      scheduleNextScan(scanLoop, Math.max(scanDelay, 300));
    }
  }, [onDetected, scanDelay, scheduleNextScan, updateUiState]);

  useEffect(() => {
    isMountedRef.current = true;

    async function setup() {
      updateUiState('initializing');
      setErrMsg('');

      const Detector = globalThis?.BarcodeDetector;

      if (typeof window !== 'undefined' && !window.isSecureContext) {
        setErrMsg('Der Kamera-Scanner benötigt eine sichere HTTPS-Verbindung. Du kannst den EAN-Code stattdessen manuell eingeben.');
        updateUiState('error');
        return;
      }

      if (!navigator?.mediaDevices?.getUserMedia || typeof Detector === 'undefined') {
        setErrMsg('Der Kamera-Scanner ist auf diesem Gerät nicht verfügbar. Du kannst den EAN-Code stattdessen manuell eingeben.');
        updateUiState('error');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });

        if (!isMountedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        const video = videoRef.current;
        if (!video) {
          stopStream();
          return;
        }

        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');
        await video.play();

        const formats = await resolveSupportedFormats();
        detectorRef.current = new Detector({ formats });

        drawOverlay('searching');
        updateUiState('searching');
        scanLoop();
      } catch (error) {
        console.error(error);
        const message =
          error?.name === 'NotAllowedError'
            ? 'Kamera-Zugriff verweigert – bitte Berechtigung erteilen.'
            : error?.name === 'SecurityError'
              ? 'Der Kamera-Scanner benötigt eine sichere HTTPS-Verbindung. Du kannst den EAN-Code stattdessen manuell eingeben.'
              : 'Kamera konnte nicht initialisiert werden. Du kannst den EAN-Code stattdessen manuell eingeben.';

        setErrMsg(message);
        updateUiState('error');
      }
    }

    setup();

    const handleResize = () => {
      const { scannerState: currentState, candidateRect } = overlayStateRef.current;
      drawOverlay(currentState || 'searching', candidateRect || null);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      isMountedRef.current = false;
      isLockedRef.current = false;
      window.removeEventListener('resize', handleResize);
      stopStream();
    };
  }, [drawOverlay, facing, scanLoop, stopStream, updateUiState]);

  return (
    <div className="pfandpirat-scanner">
      <div className="pfandpirat-scanner__viewport">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="pfandpirat-scanner__video"
        />
        <canvas ref={overlayCanvasRef} className="pfandpirat-scanner__overlay" />

        <div className={`pfandpirat-scanner__status is-${scannerState}`}>
          <strong>{statusMeta.label}</strong>
          <span>{statusMeta.hint}</span>
        </div>

        {onClose ? (
          <button
            type="button"
            className="pfandpirat-scanner__close"
            onClick={onClose}
            aria-label="Scanner schließen"
          >
            ✕
          </button>
        ) : null}

        {errMsg ? (
          <div className="pfandpirat-scanner__error">{errMsg}</div>
        ) : null}
      </div>
    </div>
  );
}
